// src/chartRenderer.ts
function field(it, name) {
  if (it == null || !name) return void 0;
  const g = it.get;
  if (typeof g === "function") {
    try {
      const v = g.call(it, name);
      if (v !== void 0) return v;
    } catch {
    }
  }
  return it[name];
}
var PALETTE = [
  "#2b7400",
  "#1F6BB0",
  "#E0A82E",
  "#B12C82",
  "#7A2EB0",
  "#2E8FD0",
  "#8FBF5A",
  "#7E8B8F",
  "#FF4B0A",
  "#4A4F52",
  "#1FA0B5",
  "#A6A92B"
];
function num(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v ?? "").trim();
  if (s === "") return 0;
  const n = Number(s);
  if (Number.isFinite(n)) return n;
  const n2 = parseFloat(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n2) ? n2 : 0;
}
function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function isColor(s) {
  return /^#([0-9a-fA-F]{3,8})$/.test(s.trim());
}
function fmt(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ",");
}
function svgPlotBox(container, reserveLegend) {
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  if (cw >= 60 && ch >= 60) {
    const legendH = reserveLegend ? 24 : 0;
    return { w: Math.max(120, cw - 8), h: Math.max(80, ch - 8 - legendH) };
  }
  return { w: 320, h: 170 };
}
function polar(cx, cy, r, a) {
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}
function slicePath(cx, cy, R, inner, a0, a1) {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = polar(cx, cy, R, a0);
  const [x1, y1] = polar(cx, cy, R, a1);
  if (inner <= 0) {
    return `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
  }
  const [ix1, iy1] = polar(cx, cy, inner, a1);
  const [ix0, iy0] = polar(cx, cy, inner, a0);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L ${ix1.toFixed(2)} ${iy1.toFixed(2)} A ${inner} ${inner} 0 ${large} 0 ${ix0.toFixed(2)} ${iy0.toFixed(2)} Z`;
}
function legendHtml(entries, extraClass = "") {
  const rows = entries.map(
    (e) => `<li class="xone-chart-legend-item"><span class="xone-chart-swatch" style="background:${esc(e.color)}"></span><span class="xone-chart-legend-label">${esc(e.title)}</span>${e.value !== void 0 ? `<span class="xone-chart-legend-value">${esc(e.value)}</span>` : ""}</li>`
  ).join("");
  const cls = extraClass !== "" ? ` ${extraClass}` : "";
  return `<ul class="xone-chart-legend${cls}">${rows}</ul>`;
}
function renderPie(container, cfg, items) {
  const valueField = cfg.valueField ?? "";
  const slices = items.map((it, i) => {
    const colorRaw = cfg.colorField ? String(field(it, cfg.colorField) ?? "").trim() : "";
    return {
      label: cfg.labelField ? String(field(it, cfg.labelField) ?? "") : `#${i + 1}`,
      value: num(field(it, valueField)),
      color: isColor(colorRaw) ? colorRaw : PALETTE[i % PALETTE.length]
    };
  }).filter((s) => s.value > 0);
  const total = slices.reduce((a, s) => a + s.value, 0);
  const R = 54, cx = 60, cy = 60, inner = cfg.type === "ring" ? 30 : 0;
  let ang = (cfg.startAngle ?? -90) * (Math.PI / 180);
  let body = "";
  if (total <= 0) {
    body = `<circle cx="${cx}" cy="${cy}" r="${R}" fill="none" stroke="#E0E0E0" stroke-width="2"/>`;
  } else if (slices.length === 1) {
    body = inner > 0 ? `<circle cx="${cx}" cy="${cy}" r="${(R + inner) / 2}" fill="none" stroke="${esc(slices[0].color)}" stroke-width="${R - inner}"/>` : `<circle cx="${cx}" cy="${cy}" r="${R}" fill="${esc(slices[0].color)}"/>`;
  } else {
    body = slices.map((s) => {
      const a0 = ang;
      const a1 = ang + s.value / total * 2 * Math.PI;
      ang = a1;
      return `<path d="${slicePath(cx, cy, R, inner, a0, a1)}" fill="${esc(s.color)}"/>`;
    }).join("");
  }
  const svg = `<svg viewBox="0 0 120 120" preserveAspectRatio="xMidYMid meet" class="xone-chart-svg xone-chart-pie">${body}</svg>`;
  const legend = cfg.showLegend !== false ? legendHtml(
    slices.map((s) => ({ title: s.label, color: s.color, value: fmt(s.value) })),
    "xone-chart-legend-overlay"
  ) : "";
  container.classList.add("xone-chart-row");
  container.innerHTML = `${svg}${legend}`;
}
function renderLine(container, cfg, items) {
  const xs = items.map((it, i) => cfg.xField ? String(field(it, cfg.xField) ?? "") : String(i + 1));
  const series = (cfg.series ?? []).map((s, si) => ({
    title: s.title,
    color: isColor(s.color) ? s.color : PALETTE[si % PALETTE.length],
    points: items.map((it) => num(field(it, s.field)))
  }));
  const allVals = series.flatMap((s) => s.points);
  const maxY = Math.max(1, ...allVals);
  const showLegend = cfg.showLegend === true && series.length > 0;
  const { w: W, h: H } = svgPlotBox(container, showLegend);
  const padL = 32, padR = 10, padT = 10, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = items.length;
  const xOf = (i) => n > 1 ? padL + i * plotW / (n - 1) : padL + plotW / 2;
  const yOf = (v) => padT + plotH * (1 - v / maxY);
  const ticks = 4;
  let grid = "";
  for (let t = 0; t <= ticks; t++) {
    const v = maxY * t / ticks;
    const y = yOf(v);
    grid += `<line x1="${padL}" y1="${y.toFixed(1)}" x2="${W - padR}" y2="${y.toFixed(1)}" stroke="#ECECEC" stroke-width="1"/>`;
    grid += `<text x="${padL - 4}" y="${(y + 3).toFixed(1)}" text-anchor="end" class="xone-chart-axis">${esc(fmt(v))}</text>`;
  }
  const xstep = Math.max(1, Math.ceil(n / 8));
  let xlabels = "";
  for (let i = 0; i < n; i += xstep) {
    xlabels += `<text x="${xOf(i).toFixed(1)}" y="${H - padB + 14}" text-anchor="middle" class="xone-chart-axis">${esc(xs[i] ?? "")}</text>`;
  }
  const lines = series.map((s) => {
    const pts = s.points.map((v, i) => `${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(" ");
    const dots = s.points.map((v, i) => `<circle cx="${xOf(i).toFixed(1)}" cy="${yOf(v).toFixed(1)}" r="2" fill="${esc(s.color)}"/>`).join("");
    return `<polyline points="${pts}" fill="none" stroke="${esc(s.color)}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>${dots}`;
  }).join("");
  const axis = `<line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" stroke="#CFCFCF" stroke-width="1"/><line x1="${padL}" y1="${padT + plotH}" x2="${W - padR}" y2="${padT + plotH}" stroke="#CFCFCF" stroke-width="1"/>`;
  const svg = `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" class="xone-chart-svg xone-chart-line">${grid}${axis}${lines}${xlabels}</svg>`;
  const legend = showLegend ? legendHtml(series.map((s) => ({ title: s.title, color: s.color }))) : "";
  container.classList.add("xone-chart-col");
  container.innerHTML = `${svg}${legend}`;
}
function renderBar(container, cfg, items) {
  const xs = items.map((it, i) => cfg.xField ? String(field(it, cfg.xField) ?? "") : String(i + 1));
  const series = (cfg.series ?? []).map((s, si) => ({
    title: s.title,
    color: isColor(s.color) ? s.color : PALETTE[si % PALETTE.length],
    points: items.map((it) => num(field(it, s.field)))
  }));
  const maxY = Math.max(1, ...series.flatMap((s) => s.points));
  const showLegend = cfg.showLegend === true && series.length > 1;
  const { w: W, h: H } = svgPlotBox(container, showLegend);
  const padL = 32, padR = 10, padT = 10, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = items.length || 1;
  const groupW = plotW / n;
  const barW = Math.max(2, groupW * 0.8 / Math.max(1, series.length));
  const yOf = (v) => padT + plotH * (1 - v / maxY);
  let bars = "";
  for (let i = 0; i < items.length; i++) {
    const gx = padL + i * groupW + groupW * 0.1;
    series.forEach((s, si) => {
      const v = s.points[i] ?? 0;
      const h = plotH * (v / maxY);
      bars += `<rect x="${(gx + si * barW).toFixed(1)}" y="${yOf(v).toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" fill="${esc(s.color)}"/>`;
    });
  }
  let xlabels = "";
  const xstep = Math.max(1, Math.ceil(n / 8));
  for (let i = 0; i < items.length; i += xstep) {
    xlabels += `<text x="${(padL + i * groupW + groupW / 2).toFixed(1)}" y="${H - padB + 14}" text-anchor="middle" class="xone-chart-axis">${esc(xs[i] ?? "")}</text>`;
  }
  const axis = `<line x1="${padL}" y1="${padT + plotH}" x2="${W - padR}" y2="${padT + plotH}" stroke="#CFCFCF" stroke-width="1"/>`;
  const svg = `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" class="xone-chart-svg xone-chart-bar">${axis}${bars}${xlabels}</svg>`;
  const legend = showLegend ? legendHtml(series.map((s) => ({ title: s.title, color: s.color }))) : "";
  container.classList.add("xone-chart-col");
  container.innerHTML = `${svg}${legend}`;
}
function renderChart(container, config, items) {
  container.classList.add("xone-chart");
  if (config.engine === "chartjs") {
    void import("./chartRendererChartjs-S7LCOYZ4.js").then((m) => {
      void m.renderChartJs(container, config, items);
    });
    return;
  }
  if (config.type === "pie" || config.type === "ring") renderPie(container, config, items);
  else if (config.type === "bar") renderBar(container, config, items);
  else renderLine(container, config, items);
}

export {
  field,
  PALETTE,
  num,
  isColor,
  renderChart
};
//# sourceMappingURL=chunk-4RHVTNRD.js.map